import {
  Cartesian3,
  Color,
  ColorMaterialProperty,
  ConstantProperty,
  CustomDataSource,
  Entity,
  HeightReference,
  PolygonHierarchy,
  PolylineGlowMaterialProperty,
} from "cesium";
import type { Feature, FeatureCollection, Position } from "geojson";
import type { GisDataItem } from "./cesium-types";

// Define a custom entity type for our GIS features
interface GisEntity extends Entity {
  ideaId?: string;
  originalColor?: Color;
}

const GIS_DATA_SOURCE_NAME = "gisDataSource";

// Helper to get or create the GIS data source
function getOrCreateGisDataSource(viewer: any): CustomDataSource {
  let dataSource = viewer.dataSources.getByName(GIS_DATA_SOURCE_NAME)[0] as CustomDataSource;
  if (!dataSource) {
    dataSource = new CustomDataSource(GIS_DATA_SOURCE_NAME);
    viewer.dataSources.add(dataSource);
  }
  return dataSource;
}

/**
 * Clears all previously added GIS data from the viewer.
 * @param viewer The Cesium viewer instance.
 */
export function clearGisData(viewer: any) {
  if (!viewer) return;
  const dataSource = getOrCreateGisDataSource(viewer);
  dataSource.entities.removeAll();
}

/**
 * Displays GIS data on the Cesium viewer.
 * @param viewer The Cesium viewer instance.
 * @param gisDataArray An array of GIS data items to display.
 */
export function displayGisData(viewer: any, gisDataArray: GisDataItem[]) {
  if (!viewer || !gisDataArray) {
    return;
  }

  const dataSource = getOrCreateGisDataSource(viewer);
  dataSource.entities.removeAll(); // Clear existing entities before adding new ones

  const addedIdeaIds = new Set<string>();

  gisDataArray.forEach((gisItem, index) => {
    if (!gisItem || !gisItem.data || !gisItem.data.features) {
      return;
    }

    const featureCollection: FeatureCollection = gisItem.data;
    const layerType = gisItem.layerType || "default";


    // Define colors based on layer type or index
    const colors = [
      Color.BLUE,
      Color.GREEN,
      Color.RED,
      Color.YELLOW,
      Color.PURPLE,
      Color.ORANGE,
    ];
    let defaultColor = colors[index % colors.length];

    switch (layerType) {
      case "polygon":
        defaultColor = Color.BLACK.withAlpha(0.3);
        break;
      case "line":
        defaultColor = Color.BLACK;
        break;
      case "point":
        defaultColor = Color.BLACK;
        break;
    }

    featureCollection.features.forEach((feature: Feature) => {
      if (!feature.geometry) return;

      const ideaId = feature.properties?.ideaId;
      if (ideaId) {
        if (addedIdeaIds.has(ideaId)) {
          return; // Skip duplicate feature
        }
        addedIdeaIds.add(ideaId);
      }

      let positions: Position[] = [];
      const geometryType = feature.geometry.type;

      if (geometryType === "Polygon") {
        positions = feature.geometry.coordinates[0];
      } else if (geometryType === "LineString") {
        positions = feature.geometry.coordinates;
      } else if (geometryType === "Point") {
        positions = [feature.geometry.coordinates];
      } else {
        return; // Unsupported geometry type
      }

      const cesiumPositions = positions.map((coord) =>
        Cartesian3.fromDegrees(coord[0], coord[1]),
      );

      let entityColor = defaultColor;
      if (feature.properties?.color) {
        try {
          entityColor = Color.fromCssColorString(feature.properties.color);
        } catch (e) {
          /* ignore invalid color */
        }
      }

      let entity;
      const entityOptions = {
        properties: feature.properties ?? {},
        ideaId: feature.properties?.ideaId,
      };

      switch (geometryType) {
        case "Polygon":
          entity = dataSource.entities.add({
            ...entityOptions,
            name: gisItem.layerName || `Polygon ${index}`,
            polygon: {
              hierarchy: new PolygonHierarchy(cesiumPositions),
              material: entityColor.withAlpha(0.3),
              outline: true,
              outlineColor: entityColor,
              heightReference: HeightReference.CLAMP_TO_GROUND,
            },
          });
          break;
        case "LineString":
          entity = dataSource.entities.add({
            ...entityOptions,
            name: gisItem.layerName || `Line ${index}`,
            polyline: {
              positions: cesiumPositions,
              width: 4,
              material: entityColor, // Simpler material for debugging
              clampToGround: true,
            },
          });
          break;
        case "Point":
          entity = dataSource.entities.add({
            ...entityOptions,
            name: gisItem.layerName || `Point ${index}`,
            position: cesiumPositions[0],
            point: {
              pixelSize: 12,
              color: entityColor,
              outlineColor: Color.WHITE,
              outlineWidth: 1.5,
              heightReference: HeightReference.CLAMP_TO_GROUND, // More reliable
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
          });
          break;
      }
    });
  });

  // Request a re-render to show the new entities
  viewer.scene.requestRender();
}

/**
 * Sets the opacity of GIS features based on a selected ideaId.
 * @param viewer The Cesium viewer instance.
 * @param selectedIdeaId The ideaId of the feature to keep at full opacity.
 *                     If undefined, all features are reset to full opacity.
 */
export function setGisFeaturesOpacity(viewer: any, selectedIdeaId?: string) {
  if (!viewer) return;
  const dataSource = getOrCreateGisDataSource(viewer);

  dataSource.entities.values.forEach((entity: GisEntity) => {
    const isSelected = entity.ideaId === selectedIdeaId;
    const opacity = selectedIdeaId ? (isSelected ? 1.0 : 0.2) : 1.0;

    // Store original color if it doesn't exist
    if (!entity.originalColor) {
      if (entity.point?.color) {
        entity.originalColor = entity.point.color.getValue(viewer.clock.currentTime);
      } else if (entity.polyline?.material) {
        const material = entity.polyline.material as ColorMaterialProperty;
        if (material?.color) {
          entity.originalColor = material.color.getValue(viewer.clock.currentTime);
        }
      } else if (entity.polygon?.material) {
        const material = entity.polygon.material as ColorMaterialProperty;
        if (material?.color) {
          entity.originalColor = material.color.getValue(viewer.clock.currentTime);
        }
      }
    }
    
    const originalColor = entity.originalColor || Color.WHITE;

    if (entity.point) {
      entity.point.color = new ConstantProperty(originalColor.withAlpha(opacity));
      if (entity.point.outlineColor) {
        entity.point.outlineColor = new ConstantProperty(Color.WHITE.withAlpha(opacity));
      }
    } else if (entity.polyline) {
      entity.polyline.material = new ColorMaterialProperty(originalColor.withAlpha(opacity));
    } else if (entity.polygon) {
      entity.polygon.material = new ColorMaterialProperty(originalColor.withAlpha(opacity * 0.3));
      if (entity.polygon.outlineColor) {
        entity.polygon.outlineColor = new ConstantProperty(originalColor.withAlpha(opacity));
      }
    }
  });

  viewer.scene.requestRender();
} 